import React, { useEffect, useMemo, useState } from 'react';
import debounce from 'just-debounce-it';
import {
  useSearchEventAccountsLazyQuery,
  SearchEventAccountsQuery,
} from '../../../graphql/queries/searchEventAccounts.generated';
import { useAddPartnerAccountToInvoiceCollectionMutation } from '../../../graphql/mutations/addPartnerAccountToInvoiceCollection.generated';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import i18n from '@/utils/i18n';
import { LayoutStoreInstance } from '@/LayoutStore';

const SEARCH_RESULT_MAX = 20;
const MIN_SEARCH_LENGTH = 3;

type AccountResult = SearchEventAccountsQuery['searchEventAccounts'][number];

// Server-side substring search over non-event partner accounts, the same
// pattern the analytics-user picker uses. Selecting an account attaches it and
// every account below it to the collection, so the badges surface whether an
// account is a parent and how many stores it backs directly.
export function AddPartnerAccountToInvoiceCollection({
  invoiceCollectionId,
  onAdded,
}: {
  invoiceCollectionId: string;
  onAdded?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [search, { loading, data }] = useSearchEventAccountsLazyQuery();
  const [addPartnerAccount, { loading: adding }] =
    useAddPartnerAccountToInvoiceCollectionMutation();

  const debouncedSearch = useMemo(
    () =>
      debounce(async (text: string) => {
        if (!text || text.length < MIN_SEARCH_LENGTH) return;
        await search({
          fetchPolicy: 'no-cache',
          variables: {
            query: text,
            limit: SEARCH_RESULT_MAX,
            filter: { only_in_use: true, is_event_account: false },
          },
        });
      }, 300),
    [search],
  );

  useEffect(() => {
    if (searchText) {
      debouncedSearch(searchText);
    }
  }, [searchText, debouncedSearch]);

  const accounts = useMemo(() => data?.searchEventAccounts ?? [], [data]);

  useEffect(() => {
    if (
      searchText.length >= MIN_SEARCH_LENGTH &&
      (loading || accounts.length)
    ) {
      setOpen(true);
    }
  }, [searchText, loading, accounts.length]);

  const handleSelect = async (account: AccountResult) => {
    try {
      LayoutStoreInstance.startStoring();
      await addPartnerAccount({
        variables: { invoiceCollectionId, partnerAccountId: account.id },
      });
      if (onAdded) {
        onAdded();
      }
      setOpen(false);
      setSearchText('');
    } catch (e: unknown) {
      const err = e as {
        graphQLErrors?: { message?: string }[];
        message?: string;
      };
      LayoutStoreInstance.setError(
        err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          i18n.t('Common:unknownError'),
      );
    } finally {
      LayoutStoreInstance.stopStoring();
    }
  };

  return (
    <div>
      <label>{i18n.t('InvoiceCollection:addPartnerAccount')}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={adding}
            onClick={() => setOpen(true)}
          >
            {adding
              ? i18n.t('Common:loading')
              : i18n.t('Common:searchForAccount')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="tw:w-[420px] tw:p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={i18n.t('Common:searchForAccount') + '...'}
              value={searchText}
              onValueChange={setSearchText}
            />
            <CommandList>
              {loading && (
                <div className="tw:p-4 tw:text-center tw:text-sm tw:text-muted-foreground">
                  {i18n.t('Common:loading')}
                </div>
              )}
              {!loading && searchText.length < MIN_SEARCH_LENGTH && (
                <CommandEmpty>{i18n.t('Common:typeToSearch')}</CommandEmpty>
              )}
              {!loading &&
                searchText.length >= MIN_SEARCH_LENGTH &&
                accounts.length === 0 && (
                  <CommandEmpty>{i18n.t('Common:noResults')}</CommandEmpty>
                )}
              {!loading && accounts.length > 0 && (
                <CommandGroup>
                  {accounts.map((account) => (
                    <CommandItem
                      key={account.id}
                      value={`${account.name} ${account.id}`}
                      onSelect={() => handleSelect(account)}
                    >
                      <div className="tw:flex tw:flex-col tw:gap-1">
                        <div className="tw:flex tw:items-center tw:gap-1 tw:flex-wrap">
                          <span>{account.name || account.id}</span>
                          {account.has_children && (
                            <Badge variant="outline" className="tw:text-[10px]">
                              {i18n.t('InvoiceCollection:badgeParentAccount')}
                            </Badge>
                          )}
                          {account.store_count > 0 && (
                            <Badge variant="outline" className="tw:text-[10px]">
                              {i18n.t('AnalyticsUsers:badgeStores', {
                                count: account.store_count,
                              })}
                            </Badge>
                          )}
                        </div>
                        <span className="tw:text-xs tw:text-muted-foreground">
                          ID: {account.id}
                          {account.company_name
                            ? ` · ${account.company_name}`
                            : ''}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default AddPartnerAccountToInvoiceCollection;
